import { useState, useMemo, useRef, useEffect } from 'react';
import { Input, Empty } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { AntdMenuItem } from '@shared/types/menu';
import { getMenuItemKey, getMenuChildren } from '@shared/utils/menuUtils';
import './MenuSearch.css';

interface MenuSearchProps {
  items: AntdMenuItem[];
  onItemSelect?: (key: string) => void;
  onClose?: () => void;
}

/**
 * Компонент поиска по пунктам меню
 */
export function MenuSearch({ items, onItemSelect, onClose }: MenuSearchProps) {
  const { t } = useTranslation(['translation', 'menu']);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<InputRef>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Фокус на input при монтировании
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Поиск по всем пунктам меню (рекурсивно)
  const searchInMenu = (
    menuItems: AntdMenuItem[],
    query: string,
    results: Array<{ key: string; label: string; path: string[] }> = [],
    path: string[] = [],
  ): Array<{ key: string; label: string; path: string[] }> => {
    if (!query || query.length < 2) {
      return [];
    }

    const lowerQuery = query.toLowerCase();

    menuItems.forEach((item) => {
      if (!item) return;

      const key = getMenuItemKey(item);
      if (!key) return;

      const label = item && 'label' in item ? String(item.label) : '';
      const lowerLabel = label.toLowerCase();

      // Проверяем совпадение в текущем пункте
      if (lowerLabel.includes(lowerQuery)) {
        results.push({
          key,
          label,
          path: [...path, label],
        });
      }

      // Рекурсивно ищем в дочерних элементах
      const children = getMenuChildren(item);
      if (children && children.length > 0) {
        searchInMenu(children, query, results, [...path, label]);
      }
    });

    return results;
  };

  // Результаты поиска
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) {
      return [];
    }
    return searchInMenu(items, searchQuery);
  }, [items, searchQuery]);

  // Обработка выбора результата
  const handleSelect = (key: string) => {
    onItemSelect?.(key);
    setSearchQuery('');
    onClose?.();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (searchResults.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % searchResults.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (searchResults[selectedIndex]) {
            handleSelect(searchResults[selectedIndex].key);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setSearchQuery('');
          onClose?.();
          break;
      }
    };

    if (searchQuery.length >= 2) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [searchQuery, searchResults, selectedIndex, onClose]);

  if (searchResults.length === 0 && searchQuery.length < 2) {
    return null;
  }

  return (
    <div className="menu-search-container">
      <Input
        ref={inputRef}
        size="small"
        placeholder={t('menu:search_placeholder') || 'Поиск в меню...'}
        prefix={<SearchOutlined />}
        suffix={
          searchQuery ? (
            <CloseOutlined
              className="menu-search-clear"
              onClick={() => {
                setSearchQuery('');
                inputRef.current?.focus();
              }}
            />
          ) : null
        }
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setSelectedIndex(0);
        }}
        className="menu-search-input"
      />

      {searchQuery.length >= 2 && (
        <div className="menu-search-results">
          {searchResults.length > 0 ? (
            <div className="menu-search-results-list">
              {searchResults.map((result, index) => (
                <div
                  key={result.key}
                  className={`menu-search-result-item ${
                    index === selectedIndex ? 'menu-search-result-item-selected' : ''
                  }`}
                  onClick={() => handleSelect(result.key)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="menu-search-result-label">{result.label}</div>
                  {result.path.length > 1 && (
                    <div className="menu-search-result-path">
                      {result.path.slice(0, -1).join(' › ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Empty
              description={t('menu:search_no_results') || 'Ничего не найдено'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: '16px 0' }}
            />
          )}
        </div>
      )}
    </div>
  );
}

